import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { TLocalesKeys as K } from '../../../../types';
import { selectFieldState } from '../../../common/selectors/field';
import s from './styles.scss';
import { ICounterState } from './util';

export const initialCounterState: ICounterState = {
  count: 0,
  current: 0,
  description: '',
  isError: false,
};

export const CharacterCount: FC = () => {
  const { value, maxLength } = useSelector(selectFieldState);
  const [counter, setCounter] = useState<ICounterState>(initialCounterState);
  const { t } = useTranslation();
  const hasLimit = maxLength > 0;

  useEffect(() => {
    if (!hasLimit) {
      setCounter(initialCounterState);
    }
  }, [hasLimit]);

  useEffect(() => {
    const step = requestAnimationFrame(() => {
      if (!hasLimit) {
        return;
      }

      const current = value.length;
      const i = maxLength - current;

      if (i > 0) {
        const message = t<K>('characters_count', { count: i });

        setCounter({
          count: i,
          current,
          description: message,
          isError: false,
        });
      } else {
        const error = t<K>('characters_count_error', { max: maxLength });

        setCounter({
          count: i,
          current,
          description: error,
          isError: true,
        });
      }
    });

    return () => cancelAnimationFrame(step);
  }, [value, maxLength, hasLimit, t]);

  if (!hasLimit) {
    return null;
  }

  const counterText = `${counter.current}/${maxLength}`;

  const content = counter.isError ? (
    <span aria-live="assertive" role="alert">
      {counterText}
    </span>
  ) : (
    <span
      className={s.counter}
      role="progressbar"
      aria-valuenow={counter.current}
      aria-valuemin={0}
      aria-valuemax={maxLength}
      aria-valuetext={counter.description}
      data-hook="count"
    >
      {counterText}
    </span>
  );

  return (
    <Text size="tiny" weight="normal">
      <Box minWidth="48px" align="right" color={counter.isError ? 'R10' : 'D40'}>
        {content}
      </Box>
    </Text>
  );
};
